import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RefreshCcw, ShieldCheck, AlertTriangle, XCircle } from "lucide-react";

/**
 * VAULT 2.0 · Admin System Health
 *
 * Consolidated release diagnostics — read-only. Powered by
 * /api/system-health/summary and /api/system-health/go-nogo. Never
 * mutates data; never exposes PII.
 */
export default function SystemHealth() {
    const [summary, setSummary] = useState(null);
    const [gate, setGate]       = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [s, g] = await Promise.all([
                api.get("/system-health/summary").catch(() => ({ data: null })),
                api.get("/system-health/go-nogo").catch(() => ({ data: null })),
            ]);
            setSummary(s.data);
            setGate(g.data);
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const verdict = gate?.verdict || "PENDING";
    const verdictTone = verdict === "GO" ? "ok"
        : verdict === "GO WITH WARNINGS" ? "warn" : "fail";
    const VerdictIcon = verdict === "GO" ? ShieldCheck
        : verdict === "GO WITH WARNINGS" ? AlertTriangle : XCircle;

    return (
        <div className="max-w-[1200px] mx-auto p-6" data-testid="admin-system-health">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold">
                        Release Diagnostics
                    </div>
                    <h1 className="text-2xl font-semibold text-stone-900 tracking-tight mt-1">
                        System Health
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        VAULT 2.0 · RC1 — release-gate telemetry.
                    </p>
                </div>
                <button
                    onClick={load}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-stone-300 text-sm hover:bg-stone-50"
                    data-testid="system-health-refresh"
                >
                    <RefreshCcw size={13} /> Refresh
                </button>
            </div>

            {/* GO / NO-GO verdict */}
            <div
                className={`rounded-2xl p-6 mb-6 border-2 ${
                    verdictTone === "ok"   ? "border-emerald-200 bg-emerald-50/50" :
                    verdictTone === "warn" ? "border-amber-200 bg-amber-50/50"    :
                                             "border-rose-200 bg-rose-50/50"
                }`}
                data-testid="system-health-gate"
            >
                <div className="flex items-center gap-4">
                    <VerdictIcon
                        size={40}
                        strokeWidth={1.6}
                        className={
                            verdictTone === "ok"   ? "text-emerald-600" :
                            verdictTone === "warn" ? "text-amber-600"   : "text-rose-600"
                        }
                    />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-semibold">
                            Release Gate
                        </div>
                        <div className={`text-3xl font-bold tracking-tight ${
                            verdictTone === "ok"   ? "text-emerald-800" :
                            verdictTone === "warn" ? "text-amber-800"   : "text-rose-800"
                        }`} data-testid="system-health-verdict">
                            {loading ? "…" : verdict}
                        </div>
                        <div className="text-sm text-stone-600 mt-1">
                            {loading ? "Checking release gate…" : (gate?.reason || "—")}
                        </div>
                    </div>
                </div>
            </div>

            {/* Release cards */}
            <div className="grid gap-4 md:grid-cols-2">
                <Card title="Release" testId="card-release">
                    {[
                        ["Name",      summary?.release?.name],
                        ["Candidate", summary?.release?.candidate],
                        ["Build",     summary?.release?.build],
                        ["Frontend commit", summary?.release?.frontend_commit],
                        ["Backend commit",  summary?.release?.backend_commit],
                        ["Environment", summary?.release?.environment],
                    ].map(([k, v]) => (
                        <Row key={k} label={k} value={v ?? "—"} />
                    ))}
                </Card>

                <Card title="Registries" testId="card-registries">
                    {[
                        ["Feature flags (live / total)",
                         `${summary?.registry?.feature_flags_live ?? "—"} / ${summary?.registry?.feature_flags_total ?? "—"}`],
                        ["Pricing plans v2 (active)",  summary?.registry?.pricing_plans_v2_active ?? "—"],
                        ["Pricing plans v1 (active)",  summary?.registry?.pricing_plans_v1_active ?? "—"],
                        ["Design system version",      summary?.registry?.design_system_version],
                    ].map(([k, v]) => (
                        <Row key={k} label={k} value={v ?? "—"} />
                    ))}
                </Card>

                <Card title="Incidents & audit" testId="card-incidents">
                    {[
                        ["CRITICAL incidents open",  summary?.incidents?.critical_open ?? 0],
                        ["High-severity audit open", summary?.incidents?.audit_events_recent ?? 0],
                    ].map(([k, v]) => (
                        <Row key={k} label={k} value={v} />
                    ))}
                </Card>

                <Card title="Launch state" testId="card-launch">
                    {[
                        ["Status",  summary?.launch_state?.status ?? "PRE_LAUNCH"],
                        ["Release", summary?.launch_state?.release ?? "—"],
                        ["Last check", summary?.checked_at?.slice(0, 19)?.replace("T", " ") ?? "—"],
                    ].map(([k, v]) => (
                        <Row key={k} label={k} value={v ?? "—"} />
                    ))}
                </Card>
            </div>

            <p className="text-xs text-stone-500 mt-6" data-testid="system-health-note">
                Read-only diagnostics. Rollback checkpoint available via <code>launch-state</code>
                {" "}history. Follow the progressive-rollout order — Founder → Internal QA → 10% →
                50% → 100% — and monitor error / API / login failure rates after each stage.
            </p>
        </div>
    );
}

function Card({ title, children, testId }) {
    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-3">
                {title}
            </div>
            <div className="divide-y divide-stone-100">
                {children}
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between py-2 text-sm">
            <span className="text-stone-500">{label}</span>
            <span className="font-medium text-stone-900 tabular-nums">{String(value)}</span>
        </div>
    );
}
